import BusinessIcon from '@mui/icons-material/Business';

import React, { useContext } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { CompanyListDTO, IsActiveEnum } from '_lib/api/apiTypes.shim';
import { SimpleAppBar } from '_lib/components/AppBar/SimpleAppBar';
import { Avatar } from '_lib/components/Avatar/Avatar';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { LinkListItemButton } from '_lib/components/LinkListItemButton/LinkListItemButton';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemAvatar } from '_lib/components/ListItemAvatar/ListItemAvatar';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { Search } from '_lib/components/Search/Search';
import { activeEnumOptions, SelectFilter } from '_lib/components/SelectFilter/SelectFilter';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';

import { normalizers, useQueryParams } from '_lib/hooks/useQueryParams';

import { formatAddressOneLine } from '_lib/utils/helpers';

import { AppDrawerState, AppRouteContext } from '../App/app.types';
import { AuthContext } from '../auth/authContext';
import { appRoutePaths, getFullAppRoutePath, getGoBackState } from '../Nav/nav.types';
import { PagedList } from '../PagedList/PagedList';
import { RouteErrorBoundary } from '../Routing/RouteErrorBoundary';
import { ScrollableContentWrapper } from '../ScrollableContentWrapper/ScrollableContentWrapper';

type CompanyListProps = {
  appDrawerState?: AppDrawerState;
};

const CompanyList = (props: CompanyListProps) => {
  const { appDrawerState } = props;

  const navigate = useNavigate();

  const api = useContext(ApiContext);
  const { callCenterCacheResource } = useContext(AuthContext);
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  const { params, setParam, searchParams } = useQueryParams({
    searchWords: { defaultValue: '' },
    isActive: { defaultValue: IsActiveEnum.True, normalize: normalizers.enum(Object.values(IsActiveEnum)) }
  });

  const companiesQuery = api.companies.useList(
    {
      params: {
        ...params,
        callCenterId: cachedCallCenterQuery.data?.id,
        serializer: 'list'
      }
    },
    { enabled: !!cachedCallCenterQuery.data?.id }
  );

  if (companiesQuery.error) {
    return <RouteErrorBoundary appBarTitle='Companies' errorTitle='Error Fetching Companies' />;
  }

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%' }}>
      <SimpleAppBar title='Companies' onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer} />
      <Divider />
      <Stack direction='row' padding={2}>
        <SelectFilter
          label='Active/Inactive'
          initialKey={params.isActive}
          options={activeEnumOptions}
          onChange={(selectedKey: string) => setParam('isActive', selectedKey)}
          sx={{ width: '125px' }}
        />
        <Search initialValue={params.searchWords} onSearch={(searchWords) => setParam('searchWords', searchWords)} />
      </Stack>
      <Divider />
      <ScrollableContentWrapper isSavingOrLoading={companiesQuery.isFetching}>
        <PagedList
          queryResult={companiesQuery}
          renderFunction={(company: CompanyListDTO) => (
            <ListItem key={company.id} divider disablePadding>
              <LinkListItemButton
                href={`${getFullAppRoutePath(appRoutePaths.Company)}/${company.id}`}
                onClick={() => {
                  navigate(`${company.id}`, {
                    state: { goBack: getGoBackState(appRoutePaths.Company, searchParams) }
                  });
                }}>
                <ListItemAvatar>
                  <Avatar>
                    <BusinessIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <>
                      <Typography component='span' text={company.name} />
                      {!company.isActive && (
                        <Typography component='span' text='INACTIVE' color='error' sx={{ paddingLeft: 1 }} />
                      )}
                    </>
                  }
                  secondary={formatAddressOneLine(company, { includeCountry: true })}
                />
              </LinkListItemButton>
            </ListItem>
          )}
        />
      </ScrollableContentWrapper>
    </FlexBox>
  );
};

export const CompanyListRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();
  return <CompanyList appDrawerState={appDrawerState} />;
};
